import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useCartOperationStatus } from "../../../../hooks/useCartOperationStatus";
import { useCartLocationStatus } from "../../../../hooks/useCartLocationStatus";
import { calculateItemsSubtotal } from "../../../../utils/cartCalculationUtils";
import CartItem from "../CartItem/CartItem";
import DeliveryDatePicker from "../DeliveryDatePicker";
import GroupMessages, { MessageType } from "../GroupMessages/GroupMessages";
import useDeliveryDateUpdate from "../../../../hooks/useDeliveryDateUpdate";
import { calculateGroupStandardDeliveryDates } from "../../../../utils/deliveryDateUtils";
import { DateService } from "../../../../utils/dateUtils";
import { useDebounceCartUpdates } from "../../../../hooks/useDebounceCartUpdates";
import { useCartMessages } from "../../../../hooks/useCartMessages";
import styles from "./CartGroup.module.css";

interface CartProductType {
  cartCustomId: string;
  quantity: number;
  product: {
    sku?: string;
    stock: number;
    reservedStock: number;
    logistics?: {
      weight: number;
    };
  };
}

interface CartGroupType {
  id: string;
  deliveryDate?: string;
  products: CartProductType[];
  subTotal?: number;
  deliveryFee?: number;
  groupTotal?: number;
  totalWeight?: number;
  deliveryFeeZone?: {
    documentId: string;
  };
}

interface StockValidationItemType {
  cartCustomId: string;
  productId: string;
  productName: string;
  requestedQuantity: number;
  availableStock: number;
  isValid: boolean;
}

interface StockValidationGroupType {
  groupId: string;
  isValid: boolean;
  items: StockValidationItemType[];
}

interface Props {
  group: CartGroupType;
  index: number;
  onDeliveryDateChange: (groupId: string, deliveryDate: string) => void;
  refreshCart: () => void;
  handleCartAmountChange: (quantity: number, item: any) => void;
  handleRemoveItem: (item: any) => void;
  isStandbyGroup?: boolean;
  stockValidation?: StockValidationGroupType;
}

const CartGroup: React.FC<Props> = ({
  group,
  index,
  onDeliveryDateChange,
  refreshCart,
  handleCartAmountChange,
  handleRemoveItem,
  isStandbyGroup = false,
  stockValidation,
}) => {
  const [minDate, setMinDate] = useState<Date>(new Date());
  const [latestStandardDeliveryDate, setLatestStandardDeliveryDate] =
    useState<Date>(new Date());
  const [stockError, setStockError] = useState<boolean>(false);
  const [outOfStockItems, setOutOfStockItems] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(!group.deliveryDate);
  const [isFeeCalculating, setIsFeeCalculating] = useState<boolean>(false);
  const [needsDeliveryDate, setNeedsDeliveryDate] = useState<boolean>(false);
  const [canShowDatePicker, setCanShowDatePicker] = useState(false);
  const [isDatePickerExiting, setIsDatePickerExiting] = useState(false);

  const prevDeliveryDateRef = useRef<string | undefined>(group.deliveryDate);
  const prevDestinationZoneRef = useRef<string | null>(null);

  const prevOperationTypeRef = useRef<string | null>(null);
  const lastCompletedOperationRef = useRef<string | null>(null);

  const hasCalculatedMinDate = useRef<boolean>(false);

  const calculatedMinDateRef = useRef<Date | null>(null);

  const {
    messages,
    isExiting: isMessagesExiting,
    showLoadingMessage,
    showSuccessMessage,
    showErrorMessage,
    showWarningMessage,
    hideMessage,
    hideAllMessages,
    handleMessagesExited,
  } = useCartMessages();

  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );
  const destinationFeeZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );
  const hasLocationSet = useSelector(
    (state: any) =>
      !!state.location?.address && !!state.location.deliveryFeeZone?.documentId
  );

  const { locationUpdateJustCompleted } = useCartLocationStatus();
  const { isLoading: isGroupUpdating, operationType } = useCartOperationStatus(
    group.id
  );
  const { updateDeliveryDate } = useDeliveryDateUpdate();
  const {
    handleIncrease,
    handleDecrease,
    handleRemove,
    getOptimisticQuantity,
  } = useDebounceCartUpdates(1200);

  useEffect(() => {
    const fetchLatestStandardDeliveryDate = async () => {
      if (!destinationLeadTimeZoneId || !destinationFeeZoneId) {
        return;
      }

      try {
        const date = await calculateGroupStandardDeliveryDates(group, {
          deliveryLeadTimeZoneId: destinationLeadTimeZoneId,
          deliveryFeeZoneId: destinationFeeZoneId,
        });

        if (!isNaN(date.getTime())) {
          const limaDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            12,
            0,
            0
          );

          calculatedMinDateRef.current = limaDate;
          hasCalculatedMinDate.current = true;

          setMinDate(limaDate);
          setLatestStandardDeliveryDate(limaDate);
        } else {
          const todayLima = new Date();
          const noonToday = new Date(
            todayLima.getFullYear(),
            todayLima.getMonth(),
            todayLima.getDate(),
            12,
            0,
            0
          );
          setMinDate(noonToday);
          setLatestStandardDeliveryDate(noonToday);

          if (!calculatedMinDateRef.current) {
            calculatedMinDateRef.current = noonToday;
            hasCalculatedMinDate.current = true;
          }
        }
      } catch (error) {
        const todayLima = new Date();
        const noonToday = new Date(
          todayLima.getFullYear(),
          todayLima.getMonth(),
          todayLima.getDate(),
          12,
          0,
          0
        );
        setMinDate(noonToday);
        setLatestStandardDeliveryDate(noonToday);

        if (!calculatedMinDateRef.current) {
          calculatedMinDateRef.current = noonToday;
          hasCalculatedMinDate.current = true;
        }
      }
    };

    if (
      destinationLeadTimeZoneId &&
      destinationFeeZoneId &&
      (!hasCalculatedMinDate.current ||
        prevDestinationZoneRef.current !== destinationFeeZoneId)
    ) {
      if (
        prevDestinationZoneRef.current !== destinationFeeZoneId &&
        prevDestinationZoneRef.current
      ) {
        calculatedMinDateRef.current = null;
        hasCalculatedMinDate.current = false;
      }

      fetchLatestStandardDeliveryDate();
    } else if (calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }

    prevDestinationZoneRef.current = destinationFeeZoneId;
  }, [group, destinationLeadTimeZoneId, destinationFeeZoneId]);

  useEffect(() => {
    const hasStockIssue = group.products.some(
      (product: CartProductType) => product.product.stock < product.quantity
    );
    setStockError(hasStockIssue);
  }, [group.products]);

  useEffect(() => {
    if (stockValidation && !stockValidation.isValid) {
      const invalidItems = stockValidation.items
        .filter((item) => !item.isValid)
        .map((item) => item.cartCustomId);

      setOutOfStockItems(invalidItems);
      setStockError(!stockValidation.isValid);
    } else {
      setOutOfStockItems([]);
    }
  }, [stockValidation]);

  const [optimisticDeliveryDate, setOptimisticDeliveryDate] = useState<
    string | undefined
  >(group.deliveryDate);

  useEffect(() => {
    if (
      group.deliveryDate !== optimisticDeliveryDate &&
      !optimisticDeliveryDate
    ) {
      setOptimisticDeliveryDate(group.deliveryDate);
    }

    if (
      !optimisticDeliveryDate &&
      !isStandbyGroup &&
      hasLocationSet &&
      !isEditing
    ) {
      setIsEditing(true);
      setIsDatePickerExiting(false);
    }

    setNeedsDeliveryDate(
      !isStandbyGroup &&
        destinationLeadTimeZoneId &&
        !optimisticDeliveryDate &&
        hasLocationSet
    );
  }, [
    group.deliveryDate,
    optimisticDeliveryDate,
    isStandbyGroup,
    hasLocationSet,
    isEditing,
    destinationLeadTimeZoneId,
  ]);

  useEffect(() => {
    const validDeliveryDate = !isNaN(latestStandardDeliveryDate.getTime());

    setCanShowDatePicker(
      hasLocationSet &&
        validDeliveryDate &&
        !isStandbyGroup &&
        !!destinationLeadTimeZoneId
    );

    setNeedsDeliveryDate(
      !isStandbyGroup &&
        destinationLeadTimeZoneId &&
        !optimisticDeliveryDate &&
        hasLocationSet
    );
  }, [
    hasLocationSet,
    latestStandardDeliveryDate,
    isStandbyGroup,
    destinationLeadTimeZoneId,
    optimisticDeliveryDate,
  ]);

  useEffect(() => {
    if (
      prevDeliveryDateRef.current &&
      !group.deliveryDate &&
      destinationFeeZoneId &&
      prevDestinationZoneRef.current !== destinationFeeZoneId
    ) {
      setOptimisticDeliveryDate(undefined);
      setIsEditing(true);
      setIsDatePickerExiting(false);

      hasCalculatedMinDate.current = false;
    }

    prevDeliveryDateRef.current = group.deliveryDate;
    prevDestinationZoneRef.current = destinationFeeZoneId;
  }, [group.deliveryDate, destinationFeeZoneId]);

  useEffect(() => {
    if (
      locationUpdateJustCompleted &&
      !group.deliveryDate &&
      !isStandbyGroup &&
      hasLocationSet
    ) {
      setIsEditing(true);
      setIsDatePickerExiting(false);

      if (destinationLeadTimeZoneId && destinationFeeZoneId) {
        const fetchLatestStandardDeliveryDate = async () => {
          try {
            const date = await calculateGroupStandardDeliveryDates(group, {
              deliveryLeadTimeZoneId: destinationLeadTimeZoneId,
              deliveryFeeZoneId: destinationFeeZoneId,
            });

            if (!isNaN(date.getTime())) {
              const limaDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                12,
                0,
                0
              );
              setMinDate(limaDate);
              setLatestStandardDeliveryDate(limaDate);
            }
          } catch (error) {
            console.error("Error calculating new delivery dates:", error);
          }
        };

        fetchLatestStandardDeliveryDate();
      }
    }
  }, [
    locationUpdateJustCompleted,
    group.deliveryDate,
    isStandbyGroup,
    hasLocationSet,
    destinationLeadTimeZoneId,
    destinationFeeZoneId,
    group,
  ]);

  useEffect(() => {
    if (isEditing && calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }
  }, [isEditing, group.id, minDate]);

  const handleDeliveryDateSelect = async (date: string) => {
    setOptimisticDeliveryDate(date);

    setIsEditing(false);
    setIsDatePickerExiting(true);

    const dateUpdateMsgId = `date-update-${group.id}`;
    const dateSuccessMsgId = `date-success-${group.id}`;
    const dateErrorMsgId = `date-error-${group.id}`;

    showLoadingMessage(
      "Actualizando fecha de entrega...",
      false,
      8000,
      dateUpdateMsgId
    );

    try {
      const success = await updateDeliveryDate(group.id, date);
      hideMessage(dateUpdateMsgId);

      if (success) {
        showSuccessMessage(
          "¡Fecha actualizada con éxito!",
          2000,
          dateSuccessMsgId
        );
        onDeliveryDateChange?.(group.id, date);
      } else {
        showErrorMessage(
          "Error al actualizar la fecha. Inténtalo nuevamente.",
          undefined,
          3000,
          dateErrorMsgId
        );
      }
    } catch (error) {
      hideMessage(dateUpdateMsgId);
      showErrorMessage(
        "Error al actualizar la fecha. Inténtalo nuevamente.",
        undefined,
        3000,
        dateErrorMsgId
      );
    }
  };

  const handleCancel = () => {
    if (group.deliveryDate) {
      setIsDatePickerExiting(true);
      setIsEditing(false);
    } else {
      const cancelWarningMsgId = `cancel-warning-${group.id}`;

      showWarningMessage(
        "Debes seleccionar una fecha de entrega para continuar",
        undefined,
        3000,
        cancelWarningMsgId
      );
    }
  };

  const handleDatePickerExited = () => {
    setIsDatePickerExiting(false);
  };

  useEffect(() => {
    if (isGroupUpdating) {
      prevOperationTypeRef.current = operationType;
    } else if (prevOperationTypeRef.current) {
      lastCompletedOperationRef.current = prevOperationTypeRef.current;
      prevOperationTypeRef.current = null;
    }
  }, [isGroupUpdating, operationType]);

  useEffect(() => {
    if (isGroupUpdating) {
      const messageContent =
        operationType === "DATE_UPDATE"
          ? "Actualizando fecha de entrega..."
          : operationType === "FEE_CALCULATION"
          ? "Calculando costo de envío..."
          : operationType === "QUANTITY_UPDATE" ||
            operationType === "ITEM_REMOVAL"
          ? "Actualizando productos..."
          : "Actualizando carrito...";

      const hasExistingMsg = messages.some(
        (msg) =>
          msg.content === messageContent && msg.type === MessageType.LOADING
      );

      if (hasExistingMsg) return;

      const operationMsgId = `operation-${operationType}-${group.id}`;
      showLoadingMessage(messageContent, false, 8000, operationMsgId);
    } else if (lastCompletedOperationRef.current) {
      const relatedLoadingMessages = messages.filter(
        (msg) =>
          msg.type === MessageType.LOADING &&
          ((lastCompletedOperationRef.current === "DATE_UPDATE" &&
            msg.content === "Actualizando fecha de entrega...") ||
            (lastCompletedOperationRef.current === "FEE_CALCULATION" &&
              msg.content === "Calculando costo de envío...") ||
            ((lastCompletedOperationRef.current === "QUANTITY_UPDATE" ||
              lastCompletedOperationRef.current === "ITEM_REMOVAL") &&
              msg.content === "Actualizando productos...") ||
            msg.content === "Actualizando carrito...")
      );

      relatedLoadingMessages.forEach((msg) => hideMessage(msg.id));

      if (relatedLoadingMessages.length > 0) {
        const hasDateSuccessMsg = messages.some(
          (msg) =>
            msg.content === "¡Fecha actualizada con éxito!" &&
            msg.type === MessageType.SUCCESS
        );

        if (!hasDateSuccessMsg) {
          const successContent =
            lastCompletedOperationRef.current === "QUANTITY_UPDATE"
              ? "¡Productos actualizados!"
              : lastCompletedOperationRef.current === "ITEM_REMOVAL"
              ? "¡Producto eliminado!"
              : lastCompletedOperationRef.current === "FEE_CALCULATION"
              ? "¡Costos actualizados!"
              : "¡Actualizado con éxito!";

          const operationSuccessMsgId = `success-${lastCompletedOperationRef.current}-${group.id}`;
          showSuccessMessage(successContent, 2000, operationSuccessMsgId);
        }
      }

      lastCompletedOperationRef.current = null;
    }
  }, [
    isGroupUpdating,
    operationType,
    messages,
    showLoadingMessage,
    showSuccessMessage,
    hideMessage,
    group.id,
  ]);

  useEffect(() => {
    if (stockError && outOfStockItems.length > 0) {
      const stockErrorMsgId = `stock-error-${group.id}`;

      showErrorMessage(
        "Algunos productos no tienen stock suficiente",
        <button
          className={styles.removeOutOfStockButton}
          onClick={() => {
            outOfStockItems.forEach((cartCustomId) => {
              const item = group.products.find(
                (p) => p.cartCustomId === cartCustomId
              );
              if (item) {
                handleRemove(item);
              }
            });

            setOutOfStockItems([]);
            setStockError(false);
          }}
        >
          Eliminar productos sin stock
        </button>,
        0,
        stockErrorMsgId
      );
    } else if (!stockError) {
      const hasStockError = messages.some(
        (msg) => msg.content === "Algunos productos no tienen stock suficiente"
      );

      if (hasStockError) {
        hideAllMessages();
      }
    }
  }, [
    stockError,
    outOfStockItems,
    group.products,
    group.id,
    showErrorMessage,
    hideAllMessages,
    handleRemove,
    messages,
  ]);

  useEffect(() => {
    if (needsDeliveryDate && !isEditing) {
      const dateWarningMsgId = `date-warning-${group.id}`;

      showWarningMessage(
        "Debes seleccionar una fecha de entrega",
        <button
          className={styles.selectDateButton}
          onClick={() => setIsEditing(true)}
        >
          Seleccionar fecha
        </button>,
        0,
        dateWarningMsgId
      );
    } else if (!needsDeliveryDate) {
      const hasMissingDateWarning = messages.some(
        (msg) => msg.content === "Debes seleccionar una fecha de entrega"
      );

      if (hasMissingDateWarning) {
        hideAllMessages();
      }
    }
  }, [
    needsDeliveryDate,
    isEditing,
    messages,
    group.id,
    showWarningMessage,
    hideAllMessages,
  ]);

  useEffect(() => {
    return () => {
      hideAllMessages();
    };
  }, [hideAllMessages]);

  const isDatePickerVisible = useMemo(() => {
    return isEditing && !isDatePickerExiting;
  }, [isEditing, isDatePickerExiting]);

  const visibleProducts = useMemo(() => {
    return group.products.filter((product) => product.quantity > 0);
  }, [group.products]);

  const optimisticSubtotal = useMemo(() => {
    const optimisticProducts = visibleProducts.map((product) => ({
      ...product,
      quantity: getOptimisticQuantity(product.cartCustomId, product.quantity),
    }));

    return calculateItemsSubtotal(optimisticProducts || []);
  }, [visibleProducts, getOptimisticQuantity]);

  useEffect(() => {
    setIsFeeCalculating(
      isGroupUpdating &&
        (operationType === "FEE_CALCULATION" ||
          operationType === "QUANTITY_UPDATE" ||
          operationType === "ITEM_REMOVAL")
    );
  }, [isGroupUpdating, operationType]);

  const isDeliveryFeeCalculating = useMemo(() => {
    if (isStandbyGroup || !destinationFeeZoneId) return false;
    if (isFeeCalculating) return true;
    if (group.deliveryFee === undefined || group.deliveryFee === null)
      return true;
    if (group.deliveryFee === -1) return true;
    return false;
  }, [
    isStandbyGroup,
    destinationFeeZoneId,
    isFeeCalculating,
    group.deliveryFee,
  ]);

  const shouldShowPorCalcular = useMemo(() => {
    return isStandbyGroup || !destinationFeeZoneId;
  }, [isStandbyGroup, destinationFeeZoneId]);

  const isGroupTotalUpdating = useMemo(() => {
    return (
      isDeliveryFeeCalculating ||
      (isGroupUpdating && operationType === "QUANTITY_UPDATE")
    );
  }, [isDeliveryFeeCalculating, isGroupUpdating, operationType]);

  const formatDate = (date: string | undefined) => {
    if (!date) return "Fecha no seleccionada";
    if (date.includes("T") && date.includes("Z")) {
      return DateService.formatDeliveryDateForDisplay(date);
    } else if (date.includes("-")) {
      return DateService.formatDeliveryDateForDisplay(
        DateService.createDateFromApiFormat(date)
      );
    } else if (date.includes("/")) {
      const parts = date.split("/").map((part) => part.trim());
      if (parts.length === 3) {
        try {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          const dateObj = DateService.createLimaDate(year, month, day);
          return DateService.formatDeliveryDateForDisplay(dateObj);
        } catch (error) {
          return "Fecha inválida";
        }
      }
    }
    return DateService.formatDeliveryDateForDisplay(date);
  };

  const headerClasses = useMemo(() => {
    let classes = styles.header;
    if (isGroupUpdating) classes += ` ${styles.headerUpdating}`;
    if (needsDeliveryDate) classes += ` ${styles.headerNeedsDate}`;
    return classes;
  }, [isGroupUpdating, needsDeliveryDate]);

  const handleOptimisticCartAmountChange = (newQuantity: number, item: any) => {
    const currentQuantity = getOptimisticQuantity(
      item.cartCustomId,
      item.quantity
    );
    const delta = newQuantity - currentQuantity;

    if (Math.abs(delta) > 1) {
      const productId = item.cartCustomId || item.product?.id || "unknown";
      const quantityUpdateMsgId = `quantity-update-${productId}-${group.id}`;
      showLoadingMessage(
        "Actualizando productos...",
        false,
        8000,
        quantityUpdateMsgId
      );
    }

    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        handleIncrease(item);
      }
    } else if (delta < 0) {
      for (let i = 0; i < Math.abs(delta); i++) {
        handleDecrease(item);
      }
    }
  };

  const handleEditClick = () => {
    if (calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }

    setIsEditing(true);
    setIsDatePickerExiting(false);
  };

  const renderDeliveryFeeRow = () => (
    <div className={styles.row}>
      <span>Costo de envío:</span>
      <span className={styles.value}>
        {shouldShowPorCalcular ? (
          "Por calcular"
        ) : isDeliveryFeeCalculating ? (
          <span className={styles.calculatingFee}>
            <span className={styles.miniSpinner}></span>
            Calculando
          </span>
        ) : group.deliveryFee === 0 ? (
          <span className={styles.freeDelivery}>Gratis</span>
        ) : (
          `S/ ${(group.deliveryFee || 0).toFixed(2)}`
        )}
      </span>
    </div>
  );

  const renderTotalRow = () => (
    <div className={`${styles.row} ${styles.total}`}>
      <span>Total:</span>
      <span className={styles.value}>
        {shouldShowPorCalcular ? (
          "Por calcular"
        ) : isGroupTotalUpdating ? (
          <span className={styles.calculatingTotal}>
            <span className={styles.miniSpinner}></span>
            Calculando
          </span>
        ) : (
          `S/ ${(optimisticSubtotal + (group.deliveryFee || 0)).toFixed(2)}`
        )}
      </span>
    </div>
  );

  const renderDeliveryDateSection = () => {
    if (!hasLocationSet) {
      return (
        <div className={styles.noLocationWarning}>
          <span>
            Selecciona una ubicación para ver las fechas de entrega disponibles
          </span>
        </div>
      );
    }

    if (!canShowDatePicker) {
      return (
        <div className={styles.calculatingDatesMessage}>
          <span className={styles.spinningIcon}></span>
          <span>Calculando fechas de entrega disponibles...</span>
        </div>
      );
    }

    const shouldForceShowPlaceholder = !!(
      locationUpdateJustCompleted ||
      (prevDeliveryDateRef.current && !group.deliveryDate)
    );

    return (
      <div
        className={`${styles.datePickerContainer} ${
          isDatePickerExiting ? styles.exiting : ""
        }`}
        onTransitionEnd={() => {
          if (isDatePickerExiting) handleDatePickerExited();
        }}
      >
        <div className={styles.datePickerSection}>
          <p className={styles.datePickerLabel}>
            {!group.deliveryDate
              ? "Selecciona una fecha de entrega para continuar:"
              : "Selecciona la fecha de envío:"}
          </p>
          <div className={styles.datePickerControls}>
            <DeliveryDatePicker
              groupId={group.id}
              minDate={minDate}
              deliveryDate={
                shouldForceShowPlaceholder ? "" : optimisticDeliveryDate ?? ""
              }
              onDateChange={handleDeliveryDateSelect}
              forceShowPlaceholder={shouldForceShowPlaceholder}
            />
            {group.deliveryDate && (
              <button onClick={handleCancel} className={styles.cancelButton}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${styles.cartGroup} ${
        needsDeliveryDate && hasLocationSet ? styles.needsDate : ""
      } ${!hasLocationSet ? styles.noLocation : ""}`}
      data-group-id={group.id}
    >
      <div className={headerClasses}>
        <div className={styles.headerContent}>
          <h2>
            {isStandbyGroup || !destinationLeadTimeZoneId || !hasLocationSet
              ? "Esperando información de envío"
              : isEditing
              ? `Disponible desde el ${DateService.formatDeliveryDateForDisplay(
                  latestStandardDeliveryDate
                )}`
              : `Entrega el ${formatDate(optimisticDeliveryDate)}`}
          </h2>
          {!isStandbyGroup &&
            destinationLeadTimeZoneId &&
            hasLocationSet &&
            !isEditing && (
              <button
                onClick={handleEditClick}
                className={styles.addDateButton}
                aria-label={
                  optimisticDeliveryDate ? "Editar fecha" : "Seleccionar Fecha"
                }
                disabled={isGroupUpdating || !canShowDatePicker}
              >
                {optimisticDeliveryDate ? "Editar fecha" : "Seleccionar Fecha"}
              </button>
            )}
        </div>
      </div>

      {isDatePickerVisible && canShowDatePicker && renderDeliveryDateSection()}

      <div className={styles.items}>
        {visibleProducts.map((product: any) => (
          <CartItem
            key={product.cartCustomId ?? ""}
            item={{
              ...product,
              groupId: group.id,
            }}
            handleCartAmountChange={handleOptimisticCartAmountChange}
            handleRemoveItem={handleRemove}
            optimisticQuantity={getOptimisticQuantity(
              product.cartCustomId,
              product.quantity
            )}
            stockValidation={stockValidation?.items.find(
              (item) => item.cartCustomId === product.cartCustomId
            )}
            allowRemoveOnOutOfStock={true}
          />
        ))}

        {visibleProducts.length === 0 && (
          <div className={styles.emptyProducts}>
            <p>No hay productos en este grupo</p>
          </div>
        )}

        <div className={styles.groupMessagesContainer}>
          <GroupMessages
            messages={messages}
            isExiting={isMessagesExiting}
            onExited={handleMessagesExited}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.row}>
            <span>Subtotal:</span>
            <span className={styles.value}>
              {`S/ ${optimisticSubtotal.toFixed(2)}`}
            </span>
          </div>

          {renderDeliveryFeeRow()}
          {renderTotalRow()}

          {needsDeliveryDate && !isDatePickerVisible && hasLocationSet && (
            <div className={styles.dateRequiredNote}>
              <button
                onClick={() => setIsEditing(true)}
                className={styles.selectRequiredDateButton}
                disabled={!canShowDatePicker}
              >
                Seleccionar fecha de entrega para continuar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartGroup;
